// weeks.ts — week-of-month bucketing + 견적 제출 현황 aggregation (issue #105).
// Pure logic only; the dashboard section consumes these. Mirrors the date-helper
// style in data.ts (local fields, no UTC drift).
import { selectionAvgVolume } from "./cost/cncBreakdown";
import { type Quote, addDays, customerNameFrom, isoDate } from "./data";
import type { Customer } from "./data";

// 주요 프로젝트 기준 — 연평균 물량 ≥ 2,000,000 EA.
export const MAJOR_PROJECT_VOLUME = 2_000_000;

// 달력 주(일~토 구간) 버킷. 라벨은 그 주 월요일 기준 — 주간업무보고가 월요일이라
// "N번째 월요일이 속한 달의 N주차"로 센다(예: 6.28(일)~7.4(토) 주 → 월요일 6.29 → 6월 5주).
// 월요일끼리는 7일 간격이라 N번째 월요일 = ceil(월요일 일자 / 7).
function mondayOfWeek(d: Date): Date {
  return addDays(new Date(d.getFullYear(), d.getMonth(), d.getDate()), 1 - d.getDay());
}

export function weekOfMonth(d: Date): number {
  return Math.ceil(mondayOfWeek(d).getDate() / 7);
}

export interface WeekBucket {
  key: string; // 정렬·비교용 "YYYY-MM-W" (예 "2025-06-1")
  year: number;
  month: number; // 1-12
  week: number; // 1..5
  label: string; // "YYYY년 M월 N주"
}

export function weekLabel(d: Date): string {
  return bucketOf(d).label;
}

function bucketOf(d: Date): WeekBucket {
  const monday = mondayOfWeek(d);
  const year = monday.getFullYear();
  const month = monday.getMonth() + 1;
  const week = Math.ceil(monday.getDate() / 7);
  return {
    key: `${year}-${String(month).padStart(2, "0")}-${week}`,
    year,
    month,
    week,
    label: `${year}년 ${month}월 ${week}주`,
  };
}

// ISO date 문자열("YYYY-MM-DD")의 주차 버킷. 시간대 무시(로컬 자정 기준).
export function bucketOfIso(iso: string): WeekBucket {
  const [y, m, day] = iso
    .slice(0, 10)
    .split("-")
    .map((s) => Number.parseInt(s, 10));
  return bucketOf(new Date(y, m - 1, day));
}

// start~end(포함) 사이의 주차 버킷을 순서대로. 하루씩 전진하며 distinct key 수집.
export function weeksInRange(start: Date, end: Date): WeekBucket[] {
  const out: WeekBucket[] = [];
  const seen = new Set<string>();
  let cur = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const stop = isoDate(end);
  // ponytail: 일 단위 루프 — 범위가 수년이면 O(days)지만 대시보드 범위는 수개월이라 무방.
  for (let guard = 0; guard < 3700; guard++) {
    const b = bucketOf(cur);
    if (!seen.has(b.key)) {
      seen.add(b.key);
      out.push(b);
    }
    if (isoDate(cur) >= stop) break;
    cur = addDays(cur, 1);
  }
  return out;
}

// 견적 연평균 물량 = 모든 CNC 파트의 유효 연평균 물량(입력 연도 평균 × USG) 합(EA/년).
// general 견적엔 cnc 파트가 없어 0.
export function quoteAnnualVolume(q: Quote): number {
  return (q.cnc ?? []).reduce(
    (sum, part) => sum + (part.selection ? selectionAvgVolume(part.selection) : 0),
    0
  );
}

export const isMajorProject = (q: Quote): boolean => quoteAnnualVolume(q) >= MAJOR_PROJECT_VOLUME;

// 제출 견적 = draft·hold 이외(보류 건은 제출 현황 대시보드에서 제외 — 수정요청). 제출일 = created.
// ponytail: created 를 제출일로 사용 — 별도 submittedAt 필드가 생기면 교체.
export const SUBMITTED_DATE = (q: Quote): string => q.created.slice(0, 10);
export const isSubmitted = (q: Quote): boolean => q.status !== "draft" && q.status !== "hold";

// 제출일이 startIso~endIso(포함) 인 제출 견적(선택적 주요 프로젝트 필터).
// 주차 버킷이 아닌 날짜 비교 — 조회 범위를 일 단위로 지정 가능(수정요청).
export function submittedInRange(
  quotes: Quote[],
  startIso: string,
  endIso: string,
  majorOnly: boolean
): Quote[] {
  return quotes.filter((q) => {
    const d = SUBMITTED_DATE(q);
    return isSubmitted(q) && d >= startIso && d <= endIso && (!majorOnly || isMajorProject(q));
  });
}

// 주차별 제출 건수 — weeks 순서대로 0 포함.
export function countsPerWeek(
  submitted: Quote[],
  weeks: WeekBucket[]
): { bucket: WeekBucket; count: number }[] {
  const byKey: Record<string, number> = {};
  for (const q of submitted) {
    const k = bucketOfIso(SUBMITTED_DATE(q)).key;
    byKey[k] = (byKey[k] || 0) + 1;
  }
  return weeks.map((bucket) => ({ bucket, count: byKey[bucket.key] || 0 }));
}

// 제출용도별 건수 — 카드 breakdown 용.
export function countsByPurpose(submitted: Quote[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const q of submitted) out[q.purpose] = (out[q.purpose] || 0) + 1;
  return out;
}

// 특정 주차의 고객사별 제출 비중(건수 내림차순).
export function customerShareForWeek(
  quotes: Quote[],
  customers: Customer[],
  weekKey: string,
  majorOnly: boolean
): { name: string; count: number }[] {
  const byName: Record<string, number> = {};
  for (const q of quotes) {
    if (!isSubmitted(q)) continue;
    if (majorOnly && !isMajorProject(q)) continue;
    if (bucketOfIso(SUBMITTED_DATE(q)).key !== weekKey) continue;
    const name = customerNameFrom(customers, q.customerId);
    byName[name] = (byName[name] || 0) + 1;
  }
  return Object.entries(byName)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count);
}

export interface SubmissionRow {
  id: string;
  customer: string;
  project: string;
  annualVolume: number;
  purpose: string;
  submittedAt: string;
  major: boolean;
}

// 특정 주차의 프로젝트 제출 현황(표 행).
export function rowsForWeek(
  quotes: Quote[],
  customers: Customer[],
  weekKey: string,
  majorOnly: boolean
): SubmissionRow[] {
  return quotes
    .filter(
      (q) =>
        isSubmitted(q) &&
        bucketOfIso(SUBMITTED_DATE(q)).key === weekKey &&
        (!majorOnly || isMajorProject(q))
    )
    .map((q) => ({
      id: q.id,
      customer: customerNameFrom(customers, q.customerId),
      project: q.project,
      annualVolume: quoteAnnualVolume(q),
      purpose: q.purpose,
      submittedAt: SUBMITTED_DATE(q),
      major: isMajorProject(q),
    }));
}
